import i18n from 'i18n-js';
import * as types from './types';
import * as userGroupsActions from 'services/api/userGroups';
import { getErrorMessage, parsePagination, isCancel } from 'utils';
import { uiActions } from 'state/modules/UI';

// Get User Groups
const fetchUsersGroupsRequest = () => ({
  type: types.FETCH_USERS_GROUPS_REQUEST,
});

const fetchUsersGroupsSuccess = (payload) => ({
  type: types.FETCH_USERS_GROUPS_SUCCESS,
  payload,
});

const fetchUsersGroupsFailed = () => ({
  type: types.FETCH_USERS_GROUPS_FAILED,
});

export const fetchUserGroups = (params, type = 'driver', options) => {
  return async (dispatch) => {
    dispatch(fetchUsersGroupsRequest());

    const response =
      type === 'business'
        ? await userGroupsActions.fetchUserGroupsBusiness(params)
        : await userGroupsActions.fetchUserGroupsList(params, options);

    if (response.ok) {
      const pagination = parsePagination(response.headers);
      return dispatch(
        fetchUsersGroupsSuccess({
          pagination: pagination,
          data: response.data,
        })
      );
    }

    if (!isCancel(response)) {
      const error = getErrorMessage(response.data);

      dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: error,
        })
      );

      dispatch(fetchUsersGroupsFailed(error));
    }
  };
};
// Get user group by ID
const fetchUserGroupByIdRequest = () => ({
  type: types.FETCH_USER_GROUP_BY_ID_REQUEST,
});

const fetchUserGroupByIdSuccess = (payload, page = 'details') => ({
  type: types.FETCH_USER_GROUP_BY_ID_SUCCESS,
  payload,
  page,
});

const fetchUserGroupByIdFailed = () => ({
  type: types.FETCH_USER_GROUP_BY_ID_FAILED,
});

export const fetchUserGroupById = (id, page = 'details', type = 'driver') => {
  return async (dispatch) => {
    dispatch(fetchUserGroupByIdRequest());

    const response =
      type === 'business'
        ? await userGroupsActions.fetchUserGroupBusinessById(id)
        : await userGroupsActions.fetchUserGroupById(id);

    if (response.ok) {
      return dispatch(fetchUserGroupByIdSuccess(response.data, page));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchUserGroupByIdFailed(error));
  };
};
// Onboarding
const onExit = (payload) => ({
  type: types.SET_EXIT,
  payload,
});

export const setExit = (exit, type) => (dispatch) => {
  return dispatch(onExit({ exit, type }));
};

const onNextPosition = (payload) => ({
  type: types.CHANGE_POSITION,
  payload,
});

export const changePosition = (type, exit) => (dispatch, getState) => {
  if (exit) {
    return dispatch(setExit(exit, 'exitOnboarding'));
  }

  const {
    onboarding: { position },
  } = getState().userGroups;

  const nextPosition = type === 'next' ? (!position ? 1 : 1) : -1;

  return dispatch(onNextPosition({ position: position + nextPosition }));
};
// Reset onboarding
export const resetOnboarding = () => (dispatch) => {
  return dispatch({ type: types.RESET_ONBOARDING });
};
// Create user group
const fetchCreateUserGroupRequest = () => ({
  type: types.FETCH_CREATE_USER_GROUP_REQUEST,
});

const fetchCreateUserGroupSuccess = (payload) => ({
  type: types.FETCH_CREATE_USER_GROUP_SUCCESS,
  payload,
});

const fetchCreateUserGroupFailed = (payload) => ({
  type: types.FETCH_CREATE_USER_GROUP_FAILED,
  payload,
});

export const fetchCreateUserGroup = (payload, type, step, exit) => async (dispatch, getState) => {
  const {
    onboarding: { steps },
  } = getState().userGroups;
  dispatch(fetchCreateUserGroupRequest());

  let response, responseVG;
  const id = payload?.id || steps?.details?.id;
  if (id) {
    response =
      type === 'business'
        ? await userGroupsActions.updateUserGroupBusiness(id, payload)
        : await userGroupsActions.updateUserGroup(id, payload);
  } else {
    response =
      type === 'business'
        ? await userGroupsActions.createUserGroupBusiness(payload)
        : await userGroupsActions.createUserGroup(payload);
  }

  if (response.ok) {
    if (id) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('user_groups.update_user_group_success'),
        })
      );
    }
    dispatch(
      fetchCreateUserGroupSuccess({
        data: response.data,
        step,
      })
    );
    return dispatch(changePosition('next', type === 'business' ? true : exit));
  }

  const formattedError = getErrorMessage(responseVG?.data || response?.data, {}, true);

  if (typeof formattedError === 'string') {
    dispatch(fetchCreateUserGroupFailed());

    return dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError,
      })
    );
  }

  let errors = {};

  formattedError?.length &&
    formattedError.map((val) => {
      return (errors[val.field] = val.message);
    });

  dispatch(fetchCreateUserGroupFailed({ errors }));
};

// Reset update user group
export const resetUpdateUserGroup = () => (dispatch) => {
  return dispatch({ type: types.RESET_UPDATE_USER_GROUP });
};

const updateStepListsRequest = (payload) => ({
  type: types.UPDATE_STEPS_LISTS_REQUEST,
  payload,
});

const updateStepListsSuccess = (payload) => ({
  type: types.UPDATE_STEPS_LISTS_SUCCESS,
  payload,
});

const updateStepListsFailed = (payload) => ({
  type: types.UPDATE_STEPS_LISTS_FAILED,
  payload,
});

export const fetchAvailablesParents =
  (params = {}, step = 'parents', listName = 'availables') =>
  async (dispatch) => {
    dispatch(updateStepListsRequest({ step, listName }));

    const response = await userGroupsActions.fetchUserGroupsList(params);

    if (response.ok) {
      const pagination = parsePagination(response.headers);
      return dispatch(
        updateStepListsSuccess({
          data: {
            list: response.data,
            pagination: pagination,
          },
          listName,
          step,
        })
      );
    }

    const formattedError = getErrorMessage(response.data);

    dispatch(updateStepListsFailed({ errors: formattedError, step, listName }));

    if (typeof formattedError === 'string') {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError,
        })
      );
    }
  };

export const fetchParents =
  (userGroupId, params = {}, step = 'parents', listName = 'parents') =>
  async (dispatch) => {
    dispatch(updateStepListsRequest({ step, listName }));

    const response = await userGroupsActions.fetchParents(userGroupId, params);

    if (response.ok) {
      const pagination = parsePagination(response.headers);
      return dispatch(
        updateStepListsSuccess({
          data: {
            list: response.data,
            pagination: pagination,
          },
          listName,
          step,
        })
      );
    }

    const formattedError = getErrorMessage(response.data);

    dispatch(updateStepListsFailed({ errors: formattedError, step, listName }));

    if (typeof formattedError === 'string') {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError,
        })
      );
    }
  };

const fetchAddItemToUserGroupRequest = (payload) => ({
  type: types.FETCH_ADD_ITEM_TO_USER_GROUP_REQUEST,
  payload,
});

const fetchAddItemToUserGroupSuccess = (payload) => ({
  type: types.FETCH_ADD_ITEM_TO_USER_GROUP_SUCCESS,
  payload,
});

const fetchAddItemToUserGroupFailed = (payload) => ({
  type: types.FETCH_ADD_ITEM_TO_USER_GROUP_FAILED,
  payload,
});

export const fetchAddParetToUserGroup = (id, payload) => async (dispatch, getState) => {
  dispatch(fetchAddItemToUserGroupRequest());

  const response = await userGroupsActions.fetchAddParetToUserGroup(id, payload);

  if (response.ok) {
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'success',
        message: 'Parent added successfully to user group',
      })
    );
    return dispatch(fetchAddItemToUserGroupSuccess());
  }

  const formattedError = getErrorMessage(response.data);

  if (typeof formattedError === 'string') {
    return dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError,
      })
    );
  }

  dispatch(fetchAddItemToUserGroupFailed({ errors: formattedError }));
};

const fetchRemoveParentFromUserGroupRequest = (payload) => ({
  type: types.FETCH_REMOVE_PARENT_FROM_USER_GROUP_REQUEST,
  payload,
});

const fetchRemoveParentFromUserGroupSuccess = (payload) => ({
  type: types.FETCH_REMOVE_PARENT_FROM_USER_GROUP_SUCCESS,
  payload,
});

const fetchRemoveParentFromUserGroupFailed = (payload) => ({
  type: types.FETCH_REMOVE_PARENT_FROM_USER_GROUP_FAILED,
  payload,
});

export const fetchRemoveParentFromUserGroup = (id, payload) => async (dispatch) => {
  dispatch(fetchRemoveParentFromUserGroupRequest());

  const response = await userGroupsActions.fetchRemoveParetFromUserGroup(id, payload);

  if (response.status === 200 || response.status === 204) {
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'success',
        message: 'Parent removed successfully',
      })
    );
    return dispatch(fetchRemoveParentFromUserGroupSuccess());
  }

  const formattedError = getErrorMessage(response.data);

  if (typeof formattedError === 'string') {
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError,
      })
    );
  }

  dispatch(fetchRemoveParentFromUserGroupFailed({ errors: formattedError }));
};

const deleteUserGroupRequest = (payload) => ({
  type: types.DELETE_USER_GROUP_REQUEST,
  payload,
});

const deleteUserGroupSuccess = (payload) => ({
  type: types.DELETE_USER_GROUP_SUCCESS,
  payload,
});

const deleteUserGroupFailed = (payload) => ({
  type: types.DELETE_USER_GROUP_FAILED,
  payload,
});

export const deleteUserGroup = (id) => async (dispatch, getState) => {
  dispatch(deleteUserGroupRequest());

  const response = await userGroupsActions.deleteUserGroup(id);

  if (response.ok) {
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'success',
        message: i18n.t('user_groups.delete_success'),
      })
    );
    return dispatch(deleteUserGroupSuccess());
  }

  const formattedError = getErrorMessage(response.data);

  if (typeof formattedError === 'string') {
    dispatch(deleteUserGroupFailed());

    return dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError,
      })
    );
  }

  dispatch(deleteUserGroupFailed({ errors: formattedError }));
};

// Reset delete user group
export const resetDeleteUserGroup = () => (dispatch) => {
  return dispatch({ type: types.RESET_DELETE_USER_GROUP });
};
